'use client';
import { JSX, useCallback, useEffect, useRef } from 'react';

// Components
import { AtButton, AtLink, AtText } from '@/components/atoms';
import { MlFormStep } from '../ml-form-step';

// Types
import {
  EClickableTarget,
  EClickableVariants,
} from '@/components/atoms/at-clickable/at-clickable.types';
import {
  EFormType,
  IFormField,
} from '@/components/organisms/or-lead-gen-form/or-lead-gen-form.types';
import { IMlformProps } from './ml-form.types';

// Hooks + Utils
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { sendGTMFormStartEvent } from '@/components/helpers/analytics/gtm';

// Constants
import {
  backButtonLabel,
  errorMessage,
  guidebookLinkText,
  guidebookUrl,
  nextButtonLabel,
  submitButtonLabel,
  successMessage,
  successMessageForDownloadForm,
} from './constants';

export const MlForm = (props: IMlformProps): JSX.Element => {
  const {
    formName,
    formId,
    formSteps,
    formType,
    formSchema,
    currentStep,
    isLastStep,
    isSubmitted,
    showError,
    handleSubmit: handleSubmitForm,
    handleNext,
    handlePrevious,
    returnUrl,
    userLocationOption,
    isInModal,
  } = props;

  const ctx = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      formName,
      formId,
      formType,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      buildLocation: '',
      moveInDate: '',
      isLotOwner: '',
      marketingConsent: true,
      topic: '',
      comments: '',
    },
  });
  const {
    handleSubmit,
    trigger,
    setFocus,
    formState,
    setError,
    clearErrors,
    getFieldState,
    setValue,
  } = ctx;

  useEffect(() => {
    const newLocation = userLocationOption?.value ?? '';

    setValue('buildLocation', newLocation, {
      shouldDirty: true,
    });
    clearErrors('buildLocation');
  }, [userLocationOption, setValue, clearErrors]);

  // Ref to focus on the message after submission
  const messageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const touchedFields = Object.keys(formState.touchedFields);
    if (touchedFields && touchedFields.length === 1) {
      const firstTouchedFormField = touchedFields[0];
      sendGTMFormStartEvent(formName, formId, firstTouchedFormField);
    }
  }, [formId, formName, formState]);

  useEffect(() => {
    if (formSteps.length > 1 && isInModal) {
      const firstFormField = formSteps[currentStep].formFields[0];
      setFocus(firstFormField.id);
    }
  }, [currentStep, formSteps, setFocus, isInModal]);

  // Focus on the message after submission
  useEffect(() => {
    if (isSubmitted || showError) {
      messageRef.current?.focus();
    }
  }, [isSubmitted, showError]);

  const areCurrentStepFormFieldsValid =
    useCallback(async (): Promise<boolean> => {
      const currentStepFields: Array<IFormField> =
        formSteps[currentStep].formFields;

      let isValid = true;

      for (let i = 0; i < currentStepFields.length; i++) {
        const field = currentStepFields[i];
        const fieldState = getFieldState(field.id);
        isValid = await trigger(field.id);
        if (!isValid) {
          // Set focus to the first invalid field
          setFocus(field.id);
          // Set error message for the field
          if (fieldState.error) {
            setError(field.id, {
              type: 'manual',
              message: fieldState.error.message,
            });
          }
          return false;
        } else {
          clearErrors(field.id);
        }
      }

      return isValid;
    }, [
      formSteps,
      currentStep,
      trigger,
      setFocus,
      setError,
      clearErrors,
      getFieldState,
    ]);

  const goToNextStep = useCallback(async () => {
    const validationPass = await areCurrentStepFormFieldsValid();
    if (validationPass) {
      handleNext?.();
    }
  }, [areCurrentStepFormFieldsValid, handleNext]);

  const successBlock =
    formType === EFormType.DOWNLOAD ? (
      <div
        className="text-center"
        ref={messageRef}
        tabIndex={-1}
        aria-live="polite"
        aria-atomic="true"
      >
        <AtText size="md">{successMessageForDownloadForm}</AtText>
        {/* we want to have a backup if query to CS fails */}
        <AtLink
          ariaLabel="View the guidebook"
          target={EClickableTarget.BLANK}
          href={returnUrl || guidebookUrl}
          className="mt-12"
          outline
        >
          {guidebookLinkText}
        </AtLink>
      </div>
    ) : (
      <AtText className="text-center" size="md">
        <span
          ref={messageRef}
          tabIndex={-1}
          aria-live="polite"
          aria-atomic="true"
        >
          {successMessage}
        </span>
      </AtText>
    );

  return (
    <FormProvider {...ctx}>
      {isSubmitted && isLastStep ? (
        successBlock
      ) : (
        <form onSubmit={handleSubmit(handleSubmitForm)}>
          {formSteps.map((step) => {
            const visibility =
              step.stepIndex === currentStep ? 'visible' : 'hidden';
            return (
              <MlFormStep
                key={step.key}
                formType={formType}
                formStep={step}
                className={visibility}
              />
            );
          })}

          {showError && (
            <AtText size="sm" className="mt-8 text-skin-alert">
              <span
                ref={messageRef}
                tabIndex={-1}
                aria-live="assertive"
                aria-atomic="true"
              >
                {errorMessage}
              </span>
            </AtText>
          )}
          <div className="mt-10 flex flex-row justify-start gap-x-4 md:mt-12">
            {currentStep > 0 && (
              <AtButton
                onClick={handlePrevious}
                type="cta"
                variant={EClickableVariants.PRIMARY}
                outline
                label={backButtonLabel}
              />
            )}
            {!formSteps[currentStep].submitToHubspot && !isLastStep && (
              <AtButton
                onClick={goToNextStep}
                type="cta"
                variant={EClickableVariants.PRIMARY}
                outline
                label={nextButtonLabel}
              />
            )}
            {formSteps[currentStep].submitToHubspot && (
              <AtButton
                type="cta"
                buttonType="submit"
                label={submitButtonLabel}
              />
            )}
          </div>
        </form>
      )}
    </FormProvider>
  );
};
